import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import Bell from 'lucide-react/dist/esm/icons/bell';
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list';
import Download from 'lucide-react/dist/esm/icons/download';
import FolderOpen from 'lucide-react/dist/esm/icons/folder-open';
import ListOrdered from 'lucide-react/dist/esm/icons/list-ordered';
import Settings from 'lucide-react/dist/esm/icons/settings';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import ShoppingBag from 'lucide-react/dist/esm/icons/shopping-bag';
import SlidersHorizontal from 'lucide-react/dist/esm/icons/sliders-horizontal';
import Terminal from 'lucide-react/dist/esm/icons/terminal';
import Upload from 'lucide-react/dist/esm/icons/upload';
import UserRoundCog from 'lucide-react/dist/esm/icons/user-round-cog';
import ArrowLeftRight from 'lucide-react/dist/esm/icons/arrow-left-right';
import PropTypes from 'prop-types';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator
} from '~/components/ui/command';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import api from '~/modules/services/api-service';
import AuthService from '~/modules/services/auth-service';
import RouteService from '~/modules/services/route-service';
import UserService from '~/modules/services/user-service';
import useAuth from '~/modules/hooks/use-auth';
import { PROJECTS_QUERY } from '~/pages/projects';

function isSubsequence(query, str) {
	let qi = 0;
	const q = query.toLowerCase();
	const s = str.toLowerCase();
	for(let i = 0; i < s.length && qi < q.length; i++) {
		if(s[i] === q[qi]) {
			qi++;
		}
	}
	return qi === q.length;
}

const BASE_NAV_ITEMS = [
	{ label: 'Projects', state: 'projects', icon: FolderOpen },
	{ label: 'Work Requests', state: 'work-requests', icon: ClipboardList },
	{ label: 'Shares', state: 'shares', icon: Share2 },
	{ label: 'Ingests', state: 'ingest', icon: Upload },
	{ label: 'Downloads', state: 'downloads', icon: Download },
	{ label: 'Reports', state: 'reports', icon: BarChart3 },
	{ label: 'Admin', state: 'admin', icon: Settings },
	{ label: 'Share Queue', state: 'share-queue', icon: ListOrdered },
	{ label: 'Order Queue', state: 'work-request-queue', icon: ShoppingBag },
	{ label: 'Notifications', event: 'pixwel:open-notifications', icon: Bell },
	{ label: 'Preferences', event: 'pixwel:open-preferences', icon: SlidersHorizontal },
	{ label: 'Switch User', event: 'pixwel:open-switch-user', icon: UserRoundCog, keywords: 'impersonate sudo' }
];

export default function CommandPalette({ open, onOpenChange }) {
	const [search, setSearch] = useState('');
	const { session } = useAuth();
	const isImpersonating = AuthService.isImpersonating();
	const isAdmin = UserService.isAdmin(session);
	const CONSOLE_ITEM = { label: 'Open Console', event: 'pixwel:open-admin-console', icon: Terminal, keywords: 'admin console terminal', hidden: !isAdmin };
	const NAV_ITEMS = [
		...BASE_NAV_ITEMS,
		CONSOLE_ITEM,
		...(isImpersonating ? [{ label: 'Switch Back', event: 'pixwel:switch-back', icon: ArrowLeftRight, keywords: 'stop impersonating' }] : [])
	];

	const { data } = useQuery(
		['command-palette-projects'],
		() => api.graphql.request(PROJECTS_QUERY, { archived: false }),
		{ staleTime: 5 * 60 * 1000, enabled: open }
	);

	const allProjects = data?.projects?.nodes || [];

	const navFuse = useMemo(() => new Fuse(NAV_ITEMS, {
		keys: ['label', 'keywords'],
		threshold: 0.3
	}), [isAdmin, isImpersonating]);

	const filteredNav = search
		? navFuse.search(search).map((r) => r.item)
		: NAV_ITEMS.filter((item) => !item.hidden);

	const filteredProjects = useMemo(() => {
		if(!search) {
			return allProjects;
		}

		// For short queries, require all typed chars to appear in order (subsequence)
		// so "grl" matches "Girls" but not "Regretting" (which has no 'l').
		// For longer queries, let Fuse handle typos on the full list.
		const candidates = search.length <= 4
			? allProjects.filter((p) => isSubsequence(search, p.name) || isSubsequence(search, p.studio?.name || ''))
			: allProjects;

		if(!candidates.length) {
			return [];
		}

		return new Fuse(candidates, {
			keys: ['name', 'studio.name'],
			threshold: 0.4,
			distance: 60,
			minMatchCharLength: 2
		}).search(search)
			.slice(0, 8)
			.map((r) => r.item);
	}, [search, allProjects]);

	const handleSelect = useCallback((item) => {
		if(item.event) {
			window.dispatchEvent(new CustomEvent(item.event));
		}
		else {
			RouteService.navigateTo(item.state);
		}
		onOpenChange(false);
	}, [onOpenChange]);

	useEffect(() => {
		if(!open) {
			setSearch('');
		}
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="overflow-hidden p-0 shadow-2xl rounded-xl" style={{ maxWidth: '32rem' }}>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Go to page or search projects..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>No results found.</CommandEmpty>

						{filteredNav.length > 0 && (
							<CommandGroup heading="Pages">
								{filteredNav.map((item) => (
									<CommandItem
										key={item.state || item.event}
										value={item.label}
										onSelect={() => handleSelect(item)}
									>
										<item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
										{item.label}
									</CommandItem>
								))}
							</CommandGroup>
						)}

						{filteredProjects.length > 0 && (
							<>
								<CommandSeparator />
								<CommandGroup heading="Projects">
									{filteredProjects.map((project) => (
										<CommandItem
											key={project.id}
											value={project.id}
											onSelect={() => {
												RouteService.navigateTo('project', { projectId: project.id });
												onOpenChange(false);
											}}
										>
											<FolderOpen className="mr-2 h-4 w-4 text-muted-foreground" />
											<span className="flex-1 truncate">{project.name}</span>
											{project.studio?.name && (
												<span className="ml-3 text-xs text-muted-foreground">{project.studio.name}</span>
											)}
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</DialogContent>
		</Dialog>
	);
}

CommandPalette.propTypes = {
	open: PropTypes.bool.isRequired,
	onOpenChange: PropTypes.func.isRequired
};
